import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = ['Jordan', 'Nike', 'Reebok', 'AND1', 'Converse'];
const ERAS = ['1980s', '1990s', '2000s'];
const CONDITIONS = ['Deadstock', 'VNDS', 'Good'];
const SIZES = ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13'];

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filter states from URL params
  const [filters, setFilters] = useState({
    brand: searchParams.get('brand') || '',
    era: searchParams.get('era') || '',
    condition: searchParams.get('condition') || '',
    size: searchParams.get('size') || '',
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  });

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key === 'minPrice' ? 'min_price' : key === 'maxPrice' ? 'max_price' : key, value);
      });
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      brand: '', era: '', condition: '', size: '', category: '', search: '', minPrice: '', maxPrice: ''
    });
    setSearchParams({});
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const FilterControls = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">SEARCH</label>
        <Input
          type="text"
          placeholder="Search shoes..."
          value={filters.search}
          onChange={(e) => updateFilter('search', e.target.value)}
          className="rounded-sm"
          data-testid="search-input"
        />
      </div>

      {/* Brand */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">BRAND</label>
        <Select value={filters.brand || "all"} onValueChange={(v) => updateFilter('brand', v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="brand-filter">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {BRANDS.map(brand => (
              <SelectItem key={brand} value={brand}>{brand}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Era */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">ERA</label>
        <Select value={filters.era || "all"} onValueChange={(v) => updateFilter('era', v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="era-filter">
            <SelectValue placeholder="All Eras" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Eras</SelectItem>
            {ERAS.map(era => (
              <SelectItem key={era} value={era}>{era}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Condition */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">CONDITION</label>
        <Select value={filters.condition || "all"} onValueChange={(v) => updateFilter('condition', v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="condition-filter">
            <SelectValue placeholder="All Conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            {CONDITIONS.map(condition => (
              <SelectItem key={condition} value={condition}>{condition}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Size */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">SIZE (US)</label>
        <Select value={filters.size || "all"} onValueChange={(v) => updateFilter('size', v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-sm" data-testid="size-filter">
            <SelectValue placeholder="All Sizes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            {SIZES.map(size => (
              <SelectItem key={size} value={size}>{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <label className="block font-bebas text-sm tracking-wider mb-2">PRICE RANGE</label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={filters.minPrice}
            onChange={(e) => updateFilter('minPrice', e.target.value)}
            className="rounded-sm"
          />
          <Input
            type="number"
            placeholder="Max"
            value={filters.maxPrice}
            onChange={(e) => updateFilter('maxPrice', e.target.value)}
            className="rounded-sm"
          />
        </div>
      </div>

      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full rounded-sm"
          data-testid="clear-filters"
        >
          <X className="w-4 h-4 mr-2" />
          Clear All Filters ({activeFilterCount})
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-concrete-white" data-testid="products-page">
      {/* Header */}
      <div className="bg-deep-obsidian py-16">
        <div className="max-w-7xl mx-auto px-6">
          <p className="font-bebas text-chicago-red text-lg tracking-widest">COLLECTION</p>
          <h1 className="font-anton text-4xl md:text-6xl text-white tracking-tighter uppercase">
            {filters.category || filters.brand || filters.era || 'ALL KICKS'}
          </h1>
          <p className="text-cement-grey mt-2">{total} products found</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex gap-12">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <h2 className="font-bebas text-xl tracking-wider mb-6">FILTERS</h2>
              <FilterControls />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6">
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full rounded-sm" data-testid="mobile-filter-btn">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="ml-2 bg-chicago-red text-white text-xs px-2 py-0.5 rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="font-bebas text-xl tracking-wider">FILTERS</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterControls />
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white animate-pulse">
                    <div className="aspect-square bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/4" />
                      <div className="h-5 bg-gray-200 rounded w-3/4" />
                      <div className="h-6 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="font-bebas text-2xl text-cement-grey">NO PRODUCTS FOUND</p>
                <p className="text-cement-grey mt-2">Try adjusting your filters</p>
                <Button onClick={clearFilters} className="mt-4 rounded-sm">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
